import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { saveDraft, loadDraft, clearDraft } from "@/lib/uploadDraftStore";

/**
 * UploadDraftContext — the single source of truth for the "one upload" the
 * user made on the homepage, persisted across route changes, sign-up flows,
 * and accidental refreshes (via IndexedDB).
 *
 * Locked upload philosophy:
 *   • There is only ONE upload.
 *   • Everything after that is routing.
 *   • Never upload twice.
 *   • Never ask the user to start again.
 *
 * API:
 *   const { draft, previewUrl, hasDraft, setDraft, clearDraft } = useUploadDraft();
 *
 * `draft` is a File object (or null). `previewUrl` is a blob: URL for <img src>,
 * revoked automatically when the draft changes or the provider unmounts.
 */

const UploadDraftContext = createContext(null);

export function UploadDraftProvider({ children }) {
  const [draft, setDraftState] = useState(null); // File | null
  const [previewUrl, setPreviewUrl] = useState(null);
  const [ready, setReady] = useState(false);
  const previewRef = useRef(null);

  // Rehydrate from IDB on mount (best-effort; expired drafts are auto-purged in loadDraft).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rec = await loadDraft();
      if (cancelled) return;
      if (rec && rec.file) {
        const url = URL.createObjectURL(rec.file);
        previewRef.current = url;
        setDraftState(rec.file);
        setPreviewUrl(url);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
        previewRef.current = null;
      }
    };
  }, []);

  const setDraft = useCallback(async (file) => {
    // Revoke any previous preview URL first.
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    if (!file) {
      setDraftState(null);
      setPreviewUrl(null);
      await clearDraft();
      return;
    }
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setDraftState(file);
    setPreviewUrl(url);
    // Fire-and-forget persist. Best-effort; in-memory is authoritative.
    saveDraft({ file });
  }, []);

  const clear = useCallback(async () => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    setDraftState(null);
    setPreviewUrl(null);
    await clearDraft();
  }, []);

  const value = useMemo(
    () => ({
      draft,
      previewUrl,
      hasDraft: !!draft,
      ready,
      setDraft,
      clearDraft: clear,
    }),
    [draft, previewUrl, ready, setDraft, clear],
  );

  return <UploadDraftContext.Provider value={value}>{children}</UploadDraftContext.Provider>;
}

export function useUploadDraft() {
  const ctx = useContext(UploadDraftContext);
  if (!ctx) {
    throw new Error("useUploadDraft must be used within an UploadDraftProvider");
  }
  return ctx;
}
